import React, { useState, useEffect, useRef, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useNavigate, useParams } from "react-router-dom";
import AddImage from "../../Global Functions/addImage";
import { updateCity, viewCity, viewAllCity } from "../../Redux/Actions/settingsActions";
import { useDispatch, useSelector } from "react-redux";
import { MdClose } from "react-icons/md";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import "./editCity.css";

const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "380px",
  borderRadius: "10px",
};

const defaultCenter = { lat: 28.6139, lng: 77.209 };

const EditCity = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { city } = useSelector((state) => state.city || {});
  const hasFetchedCities = useRef(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  const [cityData, setCityData] = useState({
    is_popular: false,
    is_vr_home_visit_available: false,
    image: "",
    id: id,
    description: "",
    position: 0,
    city_name: "",
    city_name_withoutspace: "",
    google_city_name: "",
    state: "",
    business_type: [],
    country: "",
    associated_cities: [], // stores { _id, city_name } objects locally
    location: {
      type: "Point",
      coordinates: [0, 0],
    },
  });

  const [isTrue, setIsTrue] = useState(false);
  const [isVRTrue, setIsVRTrue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);

  const [countryName, setCountryName] = useState("N/A");
  const [stateName, setStateName] = useState("N/A");

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (!city?.data?.length && !hasFetchedCities.current) {
      dispatch(viewAllCity());
      hasFetchedCities.current = true;
    }
  }, [dispatch, city?.data?.length]);

  useEffect(() => {
    async function fetchCity() {
      setLoading(true);
      try {
        const response = await dispatch(viewCity(id));
        if (response && response.statusCode === 200 && response.data) {
          const data = response.data;

          const coords = data.location?.coordinates || [0, 0];
          const lng = coords[0];
          const lat = coords[1];

          const normalizedAssocCities = Array.isArray(data.associated_cities)
            ? data.associated_cities.map((c) => {
              if (typeof c === "object" && c !== null) {
                return { _id: String(c._id), city_name: c.city_name || c.name || "" };
              }
              return { _id: String(c), city_name: "" };
            })
            : [];


          setCityData({
            is_popular: data.is_popular || false,
            is_vr_home_visit_available: data.is_vr_home_visit_available || false,
            image: data.image || "",
            id: data._id,
            description: data.description || "",
            position: data.position || 0,
            city_name: data.city_name || "",
            city_name_withoutspace: data.city_name_withoutspace || (data.city_name || "").replace(/\s+/g, ""),
            google_city_name: data.google_city_name || "",
            state: data.state?._id || data.state || "",
            country: data.country?._id || data.country || "",
            business_type: Array.isArray(data.business_type) ? data.business_type : [],
            associated_cities: normalizedAssocCities,
            location: data.location || { type: "Point", coordinates: [0, 0] },
          });

          setIsTrue(data.is_popular || false);
          setIsVRTrue(data.is_vr_home_visit_available || false);
          setCountryName(data.country?.country_name || data.country || "N/A");
          setStateName(data.state?.state_name || data.state || "N/A");

          if (lng !== 0 || lat !== 0) {
            setMarkerPosition({ lat, lng });
            setMapCenter({ lat, lng });
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    }
    fetchCity();
  }, [dispatch, id]);

  useEffect(() => {
    if (!city?.data?.length || !cityData.associated_cities.length) return;
    const allResolved = cityData.associated_cities.every(
      (c) => c.city_name && c.city_name.length !== 24
    );
    if (allResolved) return;
    setCityData((prev) => ({
      ...prev,
      associated_cities: prev.associated_cities.map((c) => {
        const match = city.data.find(
          (city) => String(city._id) === String(c._id)
        );
        return match ? { _id: match._id, city_name: match.city_name } : c;
      }),
    }));
  }, [city?.data, cityData.associated_cities.length]);

  useEffect(() => {
    setCityData((prev) => ({
      ...prev,
      city_name_withoutspace: prev.city_name.replace(/\s+/g, ""),
    }));
  }, [cityData.city_name]);

  // ── Map handlers ──
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setCityData((prev) => ({
      ...prev,
      location: { type: "Point", coordinates: [lng, lat] },
    }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setCityData((prev) => ({
          ...prev,
          google_city_name: results[0].formatted_address,
        }));
      }
    });
  }, []);

  const onAutocompleteLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });

        setCityData((prev) => ({
          ...prev,
          google_city_name: place.formatted_address || "",
          location: { type: "Point", coordinates: [lng, lat] },
        }));
      }
    }
  };

  const popularToggle = (event) => {
    event.preventDefault();
    const updatedStatus = !isTrue;
    setIsTrue(updatedStatus);
    setCityData((prev) => ({ ...prev, is_popular: updatedStatus }));
  };

  const vrToggle = (event) => {
    event.preventDefault();
    const updatedStatus = !isVRTrue;
    setIsVRTrue(updatedStatus);
    setCityData((prev) => ({ ...prev, is_vr_home_visit_available: updatedStatus }));
  };

  const handleImageUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;
    const responseUrl = await AddImage(uploadedFile);
    if (responseUrl) {
      setCityData((prev) => ({ ...prev, image: responseUrl }));
    }
  };

  const handleChange = (e) => {
    setCityData({ ...cityData, [e.target.name]: e.target.value });
  };

  // ── Associated Cities handlers ──
  const handleAssocCitySelect = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) return;

    const alreadyAdded = cityData.associated_cities.some((c) => c._id === selectedId);
    if (alreadyAdded) {
      e.target.value = "";
      return;
    }

    const selectedCity = city?.data?.find((c) => c._id === selectedId);
    if (!selectedCity) return;

    setCityData((prev) => ({
      ...prev,
      associated_cities: [
        ...prev.associated_cities,
        { _id: selectedCity._id, city_name: selectedCity.city_name },
      ],
    }));
    e.target.value = "";
  };

  const handleRemoveAssocCity = (cityId) => {
    setCityData((prev) => ({
      ...prev,
      associated_cities: prev.associated_cities.filter((c) => c._id !== cityId),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        ...cityData,
        associated_cities: cityData.associated_cities.map((c) => c._id),
      };

      const { id: cityId, ...bodyData } = payload; 

      const response = await dispatch(updateCity(cityId, bodyData)); 
      if (response?.statusCode === 200) {
        navigate(-1);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const hasLocation =
    cityData.location.coordinates[0] !== 0 ||
    cityData.location.coordinates[1] !== 0;

  if (loading) {
    return (
      <MainLayout>
        <div className="ecy-loading">
          <div className="ecy-loading-spinner" />
          <p>Loading city data...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="ecy-wrapper">

        {/* Header */}
        <div className="ecy-header">
          <div className="ecy-header-left">
            <div className="ecy-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div>
              <h1 className="ecy-title">Edit City</h1>
              <p className="ecy-subtitle">
                {cityData.city_name ? `Editing — ${cityData.city_name}` : "Update city details"}
              </p>
            </div>
          </div>
          <button onClick={() => navigate(-1)} className="ecy-view-btn" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Go Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ecy-form">

          {/* ── Section 01: City Information ── */}
          <div className="ecy-section">
            <div className="ecy-section-header">
              <span className="ecy-section-badge">01</span>
              <h2 className="ecy-section-title">City Information</h2>
            </div>
            <div className="ecy-grid ecy-grid-2">

              <div className="ecy-field">
                <label className="ecy-label">City Name</label>
                <input
                  className="ecy-input"
                  type="text"
                  name="city_name"
                  value={cityData.city_name}
                  onChange={handleChange}
                  placeholder="e.g. New Delhi"
                  maxLength={100}
                />
                <span className="ecy-char-count">{cityData.city_name.length}/100</span>
              </div>

              <div className="ecy-field">
                <label className="ecy-label">
                  City Name <span className="ecy-optional">(without spaces)</span>
                </label>
                <input
                  className="ecy-input ecy-input--readonly"
                  type="text"
                  value={cityData.city_name_withoutspace}
                  readOnly
                  disabled
                  placeholder="Auto-generated"
                />
                <span className="ecy-hint">Auto-filled from city name</span>
              </div>

              <div className="ecy-field ecy-field--full">
                <label className="ecy-label">Description</label>
                <textarea
                  className="ecy-textarea"
                  name="description"
                  value={cityData.description}
                  onChange={handleChange}
                  placeholder="Brief description of the city..."
                  maxLength={500}
                  rows={3}
                />
                <span className="ecy-char-count">{cityData.description.length}/500</span>
              </div>

              <div className="ecy-field">
                <label className="ecy-label">Popular Key (Position)</label>
                <input
                  className="ecy-input"
                  type="number"
                  name="position"
                  value={cityData.position}
                  onChange={handleChange}
                  placeholder="e.g. 1"
                />
              </div>

            </div>
          </div>

          {/* ── Section 02: Map & Coordinates ── */}
          <div className="ecy-section">
            <div className="ecy-section-header">
              <span className="ecy-section-badge">02</span>
              <h2 className="ecy-section-title">Map & Coordinates</h2>
            </div>

            <div className="ecy-field ecy-mb-20">
              <label className="ecy-label">Google City Name</label>
              <input
                className="ecy-input ecy-input--readonly"
                type="text"
                value={cityData.google_city_name}
                readOnly
                disabled
                placeholder="Auto-filled when location is selected"
              />
              <span className="ecy-hint">Populated automatically from map selection</span>
            </div>

            {loadError && (
              <div className="ecy-map-placeholder">
                <div className="ecy-map-placeholder-icon">🗺️</div>
                <p className="ecy-map-placeholder-title">Map unavailable</p>
                <p className="ecy-map-placeholder-desc">Check your Google Maps API key</p>
              </div>
            )}

            {!isLoaded && !loadError && (
              <div className="ecy-map-placeholder">
                <div className="ecy-map-loading-spinner" />
                <p className="ecy-map-placeholder-title">Loading map...</p>
              </div>
            )}

            {isLoaded && !loadError && (
              <div className="ecy-map-wrap">
                <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                  <input
                    type="text"
                    placeholder="Search for a place..."
                    className="ecy-input ecy-map-search"
                  />
                </Autocomplete>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={13}
                  onClick={onMapClick}
                  onLoad={onMapLoad}
                >
                  {markerPosition && <Marker position={markerPosition} />}
                </GoogleMap>
                <p className="ecy-map-hint">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Click on the map or search above to update the city location
                </p>
              </div>
            )}

            <div className="ecy-grid ecy-grid-2 ecy-mt-16">
              <div className="ecy-field">
                <label className="ecy-label">Longitude</label>
                <input
                  className="ecy-input ecy-input--readonly"
                  type="text"
                  value={hasLocation ? cityData.location.coordinates[0] : ""}
                  readOnly
                  disabled
                  placeholder="Auto-filled from map"
                />
              </div>
              <div className="ecy-field">
                <label className="ecy-label">Latitude</label>
                <input
                  className="ecy-input ecy-input--readonly"
                  type="text"
                  value={hasLocation ? cityData.location.coordinates[1] : ""}
                  readOnly
                  disabled
                  placeholder="Auto-filled from map"
                />
              </div>
            </div>
          </div>

          {/* ── Section 03: Settings ── */}
          <div className="ecy-section">
            <div className="ecy-section-header">
              <span className="ecy-section-badge">03</span>
              <h2 className="ecy-section-title">Settings</h2>
            </div>
            <div className="ecy-grid ecy-grid-2">

              <div className="ecy-toggle-card">
                <div className="ecy-toggle-info">
                  <div className="ecy-toggle-icon ecy-toggle-icon--popular">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </div>
                  <div>
                    <p className="ecy-toggle-label">Popular City</p>
                    <p className="ecy-toggle-desc">{isTrue ? "Marked as popular" : "Not marked as popular"}</p>
                  </div>
                </div>
                <label className="ecy-switch">
                  <input type="checkbox" checked={isTrue} onChange={popularToggle} />
                  <span className="ecy-switch-track"><span className="ecy-switch-thumb" /></span>
                </label>
              </div>

              <div className="ecy-toggle-card">
                <div className="ecy-toggle-info">
                  <div className="ecy-toggle-icon ecy-toggle-icon--vr">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </div>
                  <div>
                    <p className="ecy-toggle-label">VR Home Visit</p>
                    <p className="ecy-toggle-desc">{isVRTrue ? "Available in this city" : "Not available"}</p>
                  </div>
                </div>
                <label className="ecy-switch">
                  <input type="checkbox" checked={isVRTrue} onChange={vrToggle} />
                  <span className="ecy-switch-track"><span className="ecy-switch-thumb" /></span>
                </label>
              </div>
            </div>
          </div>

          {/* ── Section 04: Associated Cities ── */}
          <div className="ecy-section">
            <div className="ecy-section-header">
              <span className="ecy-section-badge">04</span>
              <h2 className="ecy-section-title">Associated Cities</h2>
            </div>

            <div className="ecy-field ecy-mb-20">
              <label className="ecy-label">Select City</label>
              <div className="ecy-assoc-select-wrap">
                <svg className="ecy-assoc-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <select
                  className="ecy-select"
                  onChange={handleAssocCitySelect}
                  defaultValue=""
                >
                  <option value="" disabled>
                    {city?.data?.length ? "Choose a city to associate..." : "Loading cities..."}
                  </option>
                  {Array.isArray(city?.data) &&
                    city.data.map((city) => (
                      <option key={city._id} value={city._id}>
                        {city.city_name}
                      </option>
                    ))}
                </select>
                <svg className="ecy-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              <span className="ecy-hint">Select from the list to add an associated city</span>
            </div>

            {/* Tags */}
            {cityData.associated_cities.length > 0 ? (
              <div className="ecy-assoc-tags">
                {cityData.associated_cities.map((city) => (
                  <span key={city._id} className="ecy-assoc-tag">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {city.city_name}
                    <button
                      type="button"
                      className="ecy-assoc-tag-remove"
                      onClick={() => handleRemoveAssocCity(city._id)}
                      title="Remove"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="ecy-assoc-empty">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <p>No associated cities added yet</p>
              </div>
            )}
          </div>

          {/* ── Section 04: Business Type ── */}
          <div className="ecy-section">
            <div className="ecy-section-header">
              <span className="ecy-section-badge">04</span>
              <h2 className="ecy-section-title">Business Type</h2>
            </div>
            <div className="ecy-grid ecy-grid-2">
              {["fresh", "resale", "business", "rental"].map((type) => (
                <label key={type} className="ecy-checkbox-card">
                  <input
                    type="checkbox"
                    checked={(cityData.business_type ?? []).includes(type)}
                    onChange={() => {
                      setCityData((prev) => {
                        const current = Array.isArray(prev.business_type) ? prev.business_type : [];
                        const already = current.includes(type);
                        return {
                          ...prev,
                          business_type: already
                            ? current.filter((t) => t !== type)
                            : [...current, type],
                        };
                      });
                    }}
                  />
                  <span className="ecy-checkbox-label">
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* ── Section 05: Image ── */}
          <div className="ecy-section">
            <div className="ecy-section-header">
              <span className="ecy-section-badge">05</span>
              <h2 className="ecy-section-title">City Image</h2>
            </div>

            <input
              style={{ display: "none" }}
              type="file"
              name="icon"
              id="ecy-icon"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
            />

            {cityData.image ? (
              <div className="ecy-image-preview">
                <img src={cityData.image} alt="City" className="ecy-preview-img" />
                <div className="ecy-image-actions">
                  <label htmlFor="ecy-icon" className="ecy-change-img-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Change Image
                  </label>
                  <button
                    type="button"
                    className="ecy-remove-img-btn"
                    onClick={() => setCityData((prev) => ({ ...prev, image: "" }))}
                    aria-label="Remove image"
                  >
                    <MdClose size={16} />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="ecy-upload-area">
                <label htmlFor="ecy-icon" className="ecy-upload-label">
                  <div className="ecy-upload-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <p className="ecy-upload-text">Click to upload city image</p>
                  <p className="ecy-upload-hint">JPG, PNG, WEBP — max 5MB</p>
                </label>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="ecy-footer">
            <button type="button" className="ecy-btn ecy-btn--cancel" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button type="submit" className="ecy-btn ecy-btn--submit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Update City
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditCity;